import type { Extent } from 'ol/extent';
import type ImageWrapper from 'ol/Image';
import ImageState from 'ol/ImageState';
import type Projection from 'ol/proj/Projection';
import ImageWMS from 'ol/source/ImageWMS';

/**
 * `ImageWMS` treats the current map viewport as the region to fetch: on
 * every render frame whose extent isn't contained by the last request, it
 * spins up a brand-new `HTMLImageElement` + decode cycle to ask "is there
 * fresh imagery for this patch of the map" - correct for a real WMS server,
 * but not for how this app uses `ImageWMS` as a conduit for a single
 * backend-pushed snapshot PNG that must stay pinned to the extent it was
 * captured at, regardless of how the user pans/zooms afterwards.
 *
 * Without this override, every pan/zoom to an extent outside the original
 * request re-triggers that fetch/decode pipeline for an image we always
 * intend to reject, which spams the console (OL logs decode failures) on
 * every affected render frame. This override keeps returning the same
 * resolved image until `requestFreshImage()` is called (when a new overlay
 * value actually arrives), so panning never invokes the loader at all - the
 * renderer keeps drawing the last image at *its own* recorded extent, which
 * is exactly how it stays visually pinned in place while the view moves.
 *
 * A failed decode is deliberately *not* pinned: an `ImageState.ERROR`
 * wrapper is handed back to `ImageWMS`, so a later request at a new extent
 * can rebuild one and retry the frame instead of being stuck on the error
 * forever. Successful (`LOADED`) frames are the ones worth pinning.
 */
export class SingleFrameImageWMS extends ImageWMS {
  private freshImageRequested = true;

  requestFreshImage() {
    this.freshImageRequested = true;
    // Drop OL's cached image so the next request rebuilds an `ImageWrapper`
    // even when the viewport extent is unchanged. Without this, a same-extent
    // request would be served straight from cache and the newly-arrived
    // overlay would never be fetched. `image` is `protected` in OL's typings
    // and always re-populated by `getImageInternal` before it is read.
    this.image = undefined as unknown as ImageWrapper;
  }

  override getImageInternal(
    extent: Extent,
    resolution: number,
    pixelRatio: number,
    projection: Projection,
  ) {
    if (
      !this.freshImageRequested &&
      this.image &&
      this.image.getState() !== ImageState.ERROR
    ) {
      return this.image;
    }
    this.freshImageRequested = false;
    return super.getImageInternal(extent, resolution, pixelRatio, projection);
  }
}
